package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

enum class LBadgeVariant {
    NEUTRAL,
    INFO,
    SUCCESS,
    WARNING,
    DANGER,
    ACCENT,
}

private data class LBadgeColors(
    val foreground: Color,
    val background: Color,
    val border: Color,
)

val LBadgeStyle = CssStyle.base {
    Modifier
        .styleModifier {
            property("display", "inline-flex")
            // A badge may now carry a dot or a spinner before its word. Centring and the gap live
            // here rather than on the slot, so a badge with nothing before its text is laid out by
            // exactly the same rules as one that has something — `gap` on a single child draws
            // nothing, which is the point.
            property("align-items", "center")
            property("gap", Space.XS.toString())
        }
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Pill)
        .padding(topBottom = Space.XS, leftRight = Space.SM)
        .fontSize(TypeScale.Micro)
        .fontWeight(600)
        .lineHeight(1)
        // A badge is the other place a status colour changes under the reader's eye, and it had the
        // snap `LProgressCellStyle` argues against for the same reason: on a running MT job the
        // receipt repolls every few seconds and this pill flips Running→Done the instant the run
        // lands, while somebody is watching it. Colour only — a pill that moved or resized would
        // shift the row it sits in.
        .transitionColors(durationMs = Motion.StandardMs)
        .whiteSpace(WhiteSpace.NoWrap)
}

@Composable
fun LBadge(
    text: String,
    modifier: Modifier = Modifier,
    variant: LBadgeVariant = LBadgeVariant.NEUTRAL,
    /**
     * Lets the label wrap onto several lines.
     *
     * A badge is a compact chip by default, and `nowrap` is what keeps a status word from breaking
     * across lines mid-syllable. But the same component also carries QA findings — whole sentences
     * containing a translator's own unbroken text — and there `nowrap` pushes the chip straight out
     * of a narrow editor column. Opt in where the content is prose rather than a label.
     */
    wrap: Boolean = false,
    /**
     * A glyph before the word, at [IconSize.SM].
     *
     * Built for `LSpinner(LSpinnerSize.SM)` on a badge that says "Running": a machine-translation
     * job's pill is the one status in the product that is true *while you are looking at it*, and a
     * word alone cannot say that it is still moving. Any glyph is allowed; the slot is a fixed
     * square either way, so a badge does not change width when its glyph does.
     */
    leading: (@Composable () -> Unit)? = null,
    /**
     * A filled circle before the word, in the badge's own tone.
     *
     * The cheaper half of [leading], and the one that belongs on a dense row: a status list reads
     * as a column of coloured words, and hard rule 2 says colour is never the only channel. The dot
     * repeats the tone as a shape, which is what makes the column scannable without a second
     * colour. It is `aria-hidden` — the word beside it already says what it means.
     *
     * Ignored when [leading] is set: two things before one word is a badge doing two jobs.
     */
    dot: Boolean = false,
) {
    val colors = variant.colors(ColorMode.current.toSitePalette())
    Span(
        LBadgeStyle.toModifier()
            .backgroundColor(colors.background)
            .color(colors.foreground)
            .border(1.px, LineStyle.Solid, colors.border)
            .then(
                if (wrap) {
                    Modifier.styleModifier {
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                    }
                } else {
                    Modifier
                },
            )
            .then(modifier)
            .toAttrs(),
    ) {
        when {
            leading != null -> Span(
                LBadgeLeadingStyle.toModifier().iconSize(IconSize.SM).toAttrs(),
            ) { leading() }

            dot -> Span(
                LBadgeDotStyle.toModifier()
                    .backgroundColor(colors.foreground)
                    .toAttrs { attr("aria-hidden", "true") },
            )
        }
        Text(text)
    }
}

/** The fixed square a badge's glyph sits in, so the pill's width does not depend on the glyph. */
val LBadgeLeadingStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("flex", "0 0 auto")
        }
    }
    // An `LIcon` draws itself at [IconSize.XL] unless told otherwise, and this slot is 14. Without
    // this the glyph is larger than the pill it sits in.
    cssRule(" > svg") {
        Modifier.width(100.percent).height(100.percent)
    }
}

/**
 * The tone repeated as a shape.
 *
 * 6px rather than a rung of [IconSize]: this is not a glyph, it is a bullet, and the smallest icon
 * step (12) beside 10.5px type reads as a circle somebody forgot to put something inside.
 */
val LBadgeDotStyle = CssStyle.base {
    Modifier
        .width(6.px)
        .height(6.px)
        .borderRadius(Radius.Pill)
        .styleModifier { property("flex", "0 0 auto") }
        // The dot flips with the pill it sits in — see [LBadgeStyle]'s note on the running job.
        .transitionColors(durationMs = Motion.StandardMs)
}

/**
 * The one colour a tone is, for a surface that wears a badge's tone without wearing a badge.
 *
 * [LStatTile] needs exactly this: a figure whose colour states a verdict, taking its hue from the
 * same six-value scale the pill beside it would. The alternative was a second `when` over
 * [LBadgeVariant] in another file, which is how "warning" ends up two different ambers.
 *
 * The foreground, not the wash: a badge's background is tuned to sit *under* 10.5px text inside a
 * 1px edge of the same hue, and a 33px figure painted in it would be barely visible.
 */
fun LBadgeVariant.toneColor(palette: SitePalette): Color = colors(palette).foreground

private fun LBadgeVariant.colors(palette: SitePalette): LBadgeColors {
    if (this == LBadgeVariant.NEUTRAL) {
        return LBadgeColors(
            foreground = palette.textSecondary,
            background = palette.surfaceSunken,
            border = palette.border,
        )
    }
    if (this == LBadgeVariant.ACCENT) {
        return LBadgeColors(
            foreground = palette.accent,
            background = palette.accentSubtle,
            border = palette.accent.tintOn(palette.surface, 0.45f),
        )
    }

    val tone = when (this) {
        LBadgeVariant.INFO -> palette.info
        LBadgeVariant.SUCCESS -> palette.success
        LBadgeVariant.WARNING -> palette.warning
        LBadgeVariant.DANGER -> palette.danger
        LBadgeVariant.NEUTRAL, LBadgeVariant.ACCENT -> error("Handled above")
    }
    return LBadgeColors(
        foreground = tone,
        // Opaque, so a badge is the same colour on a card, on the page and on a zebra
        // row — and so its own text keeps a contrast that does not depend on what is behind it.
        background = tone.tintOn(palette.surface, 0.12f),
        border = tone.tintOn(palette.surface, 0.45f),
    )
}
